// Import React and hooks for managing component state and lifecycle
import React, { useState, useEffect } from 'react';
// Import icon components from Lucide React for the user interface
import { ClipboardList, Edit2, Save, X, Search } from 'lucide-react';
// Import database utilities for querying data and executing SQL
import { db, sql } from '../../lib/db';
// Import toast notification system for user feedback
import { toast } from 'sonner';

// Define props this SOP settings component expects
interface SOPSettingsProps {
  // userId: Unique identifier for the current user
  userId: string;
}

// Define structure for regular task templates (recurring daily/weekly tasks)
interface RegularTaskTemplate {
  id: string;                    // Unique identifier
  title: string;                 // Task name
  description?: string;          // Optional task description
  frequency: string;             // How often it recurs (daily, weekly, etc.)
  sop?: string;                  // Standard Operating Procedure text
  instances: number;             // How many instances of this task exist
  type: 'regular';               // Discriminator for TypeScript type checking
}

// Define structure for template tasks (tasks from goal templates)
interface TemplateTaskWithSOP {
  id: string;                    // Unique identifier
  title: string;                 // Task name
  description?: string;          // Optional task description
  sop: string;                   // Standard Operating Procedure text (required)
  goal_template_title: string;   // Name of the goal template this task belongs to
  goal_template_id: string;      // ID of the goal template
  type: 'template';              // Discriminator for TypeScript type checking
}

// Define structure for deliverable tasks (tasks from instantiated deliverables)
interface DeliverableTaskWithSOP {
  id: string;                    // Unique identifier
  title: string;                 // Task name
  description?: string;          // Optional task description
  sop: string;                   // Standard Operating Procedure text (required)
  goal_template_title: string;   // Name of the deliverable this task belongs to
  goal_template_id: string | null; // ID of original template (may be null for custom deliverables)
  instances: number;             // How many instances of this task exist
  type: 'deliverable';           // Discriminator for TypeScript type checking
}

// Union type combining all possible task types that can have SOPs
// This allows TypeScript to understand that a task can be any of these three types
type TaskWithSOP = RegularTaskTemplate | TemplateTaskWithSOP | DeliverableTaskWithSOP;

// SOPSettings component allows users to manage Standard Operating Procedures
// SOPs are step-by-step instructions for completing tasks consistently
export function SOPSettings({ userId }: SOPSettingsProps) {
  // State to store all tasks that have or can have SOPs
  const [tasks, setTasks] = useState<TaskWithSOP[]>([]);
  // State to show loading indicator while fetching data
  const [loading, setLoading] = useState(true);
  // State to track which task is currently being edited (if any)
  const [editingId, setEditingId] = useState<string | null>(null);
  // State to track what type of task is being edited
  const [editingType, setEditingType] = useState<'regular' | 'template' | null>(null);
  // State to store the SOP text while user is editing it
  const [editingSOP, setEditingSOP] = useState('');
  // State to store the search filter text
  const [searchTerm, setSearchTerm] = useState('');

  // useEffect runs when component mounts or when userId changes
  // This ensures we load the correct tasks for the current user
  useEffect(() => {
    loadTasks(); // Load tasks from database
  }, [userId]); // Re-run if userId changes

  // Function to load all tasks that have SOPs from the database
  // Uses complex SQL queries to fetch from different sources
  const loadTasks = async () => {
    try {
      setLoading(true); // Show loading indicator
      
      // Execute three SQL queries in parallel for better performance
      // Promise.all runs all queries simultaneously rather than sequentially
      const [regularResult, templateResult, deliverableResult] = await Promise.all([
        // Get unique regular task templates
        sql`
          WITH regular_task_templates AS (
            SELECT 
              title,
              frequency,
              -- Get the SOP from any instance that has one (prefer non-null SOPs)
              COALESCE(
                (array_agg(sop ORDER BY CASE WHEN sop IS NOT NULL THEN 0 ELSE 1 END, created_at DESC))[1],
                NULL
              ) as sop,
              -- Get the most recent task ID
              (array_agg(id ORDER BY created_at DESC))[1] as id,
              -- Get description from most recent task (ignoring completion notes)
              (array_agg(
                CASE 
                  WHEN description LIKE '%Completion Notes:%' THEN ''
                  ELSE COALESCE(description, '')
                END 
                ORDER BY created_at DESC
              ))[1] as description,
              COUNT(*) as instances
            FROM tasks
            WHERE user_id = ${userId}
            AND is_regular = true
            AND status != 'Superseded'
            AND status IS NOT NULL
            GROUP BY title, frequency
          )
          SELECT *, 'regular' as type FROM regular_task_templates
          WHERE sop IS NOT NULL
          ORDER BY title, frequency
        `,
        // Get template tasks with SOPs
        sql`
          SELECT 
            tt.id,
            tt.title,
            tt.description,
            tt.sop,
            gt.title as goal_template_title,
            gt.id as goal_template_id,
            'template' as type
          FROM task_templates tt
          JOIN goal_templates gt ON tt.goal_template_id = gt.id
          WHERE tt.sop IS NOT NULL
          AND (
            (gt.is_system = true AND gt.id NOT IN (
              SELECT goal_template_id FROM user_hidden_templates WHERE user_id = ${userId}
            ))
            OR (gt.user_id = ${userId})
          )
          ORDER BY gt.title, tt.position
        `,
        // Get deliverable tasks with SOPs (tasks from instantiated templates)
        sql`
          WITH deliverable_tasks AS (
            SELECT 
              t.title,
              -- Group by title to avoid showing duplicates
              COALESCE(
                (array_agg(t.sop ORDER BY CASE WHEN t.sop IS NOT NULL THEN 0 ELSE 1 END, t.created_at DESC))[1],
                NULL
              ) as sop,
              (array_agg(t.id ORDER BY t.created_at DESC))[1] as id,
              (array_agg(
                CASE 
                  WHEN t.description LIKE '%Completion Notes:%' THEN ''
                  ELSE COALESCE(t.description, '')
                END 
                ORDER BY t.created_at DESC
              ))[1] as description,
              COUNT(*) as instances,
              (array_agg(g.title ORDER BY t.created_at DESC))[1] as goal_title
            FROM tasks t
            JOIN goals g ON t.goal_id = g.id
            WHERE t.user_id = ${userId}
            AND t.is_regular = false
            AND t.status != 'Superseded'
            AND t.status IS NOT NULL
            AND t.sop IS NOT NULL
            GROUP BY t.title
          )
          SELECT 
            id,
            title,
            description,
            sop,
            goal_title as goal_template_title,
            null as goal_template_id,
            'deliverable' as type,
            instances
          FROM deliverable_tasks
          ORDER BY title
        `
      ]);
      
      const allTasks = [
        ...(regularResult || []).map(t => ({ ...t, type: 'regular' as const })),
        ...(templateResult || []).map(t => ({ ...t, type: 'template' as const })),
        ...(deliverableResult || []).map(t => ({ ...t, type: 'deliverable' as const }))
      ];
      
      setTasks(allTasks as TaskWithSOP[]);
    } catch (error) {
      console.error('Error loading tasks:', error);
      toast.error('Failed to load tasks with SOPs');
    } finally {
      setLoading(false);
    }
  };

  const startEditing = (task: TaskWithSOP) => {
    setEditingId(task.id);
    setEditingType(task.type);
    setEditingSOP(task.sop || '');
  };

  const cancelEditing = () => {
    setEditingId(null);
    setEditingType(null);
    setEditingSOP('');
  };

  const saveSOP = async (task: TaskWithSOP) => {
    try {
      if (task.type === 'regular') {
        const success = await db.tasks.updateRegularTaskSOP(task.id, editingSOP.trim() || null, userId);
        
        if (success) {
          toast.success(`SOP updated for all ${task.instances} instances of "${task.title}"`);
          setEditingId(null);
          setEditingType(null);
          setEditingSOP('');
          loadTasks(); // Reload to show updated data
        } else {
          toast.error('Failed to update SOP');
        }
      } else if (task.type === 'template') {
        // Update template task SOP
        const success = await db.taskTemplates.update(task.id, userId, { sop: editingSOP.trim() || null });
        
        if (success) {
          toast.success(`SOP updated for template task "${task.title}"`);
          setEditingId(null);
          setEditingType(null);
          setEditingSOP('');
          loadTasks(); // Reload to show updated data
        } else {
          toast.error('Failed to update template SOP');
        }
      } else {
        // Update deliverable task SOP - update all tasks with same title
        const success = await db.tasks.updateDeliverableTaskSOP(task.title, editingSOP.trim() || null, userId);
        
        if (success) {
          toast.success(`SOP updated for all instances of "${task.title}"`);
          setEditingId(null);
          setEditingType(null);
          setEditingSOP('');
          loadTasks(); // Reload to show updated data
        } else {
          toast.error('Failed to update deliverable task SOP');
        }
      }
    } catch (error) {
      console.error('Error updating SOP:', error);
      toast.error('Failed to update SOP');
    }
  };

  const filteredTasks = tasks.filter(task => {
    const searchLower = searchTerm.toLowerCase();
    const titleMatch = task.title.toLowerCase().includes(searchLower);
    const descriptionMatch = task.description && task.description.toLowerCase().includes(searchLower);
    const templateMatch = (task.type === 'template' || task.type === 'deliverable') && task.goal_template_title.toLowerCase().includes(searchLower);
    return titleMatch || descriptionMatch || templateMatch;
  });

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-gray-500">Loading tasks with SOPs...</div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div>
        <h3 className="text-lg font-semibold text-gray-900 mb-2">Standard Operating Procedures</h3>
        <p className="text-sm text-gray-600">
          Manage SOPs for regular tasks, template tasks, and deliverable tasks. Changes apply to all instances.
        </p>
      </div>

      <div className="relative">
        <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
        <input
          type="text"
          placeholder="Search tasks with SOPs..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
        />
      </div>

      {filteredTasks.length === 0 ? (
        <div className="bg-gray-50 rounded-lg p-8 text-center">
          <ClipboardList className="w-12 h-12 text-gray-400 mx-auto mb-3" />
          <p className="text-gray-600">
            {searchTerm ? 'No tasks with SOPs found matching your search.' : 'No tasks with SOPs found. Add SOPs in Focus Mode or to template tasks in Templates.'}
          </p>
        </div>
      ) : (
        <div className="space-y-4">
          {filteredTasks.map((task) => (
            <div key={task.id} className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
              <div className="flex justify-between items-start mb-2">
                <div className="flex-1">
                  <h4 className="font-medium text-gray-900">{task.title}</h4>
                  {task.description && (
                    <p className="text-sm text-gray-600 mt-1">{task.description}</p>
                  )}
                  <div className="flex gap-4 mt-2 text-sm text-gray-500">
                    {task.type === 'regular' ? (
                      <>
                        <span className="inline-flex items-center px-2 py-1 rounded-md bg-gray-100">
                          {task.frequency}
                        </span>
                        <span>{task.instances} instance{task.instances !== 1 ? 's' : ''}</span>
                      </>
                    ) : task.type === 'template' ? (
                      <>
                        <span className="inline-flex items-center px-2 py-1 rounded-md bg-purple-100 text-purple-700">
                          Template Task
                        </span>
                        <span className="text-gray-600">from "{task.goal_template_title}"</span>
                      </>
                    ) : (
                      <>
                        <span className="inline-flex items-center px-2 py-1 rounded-md bg-blue-100 text-blue-700">
                          Deliverable Task
                        </span>
                        <span className="text-gray-600">from "{task.goal_template_title}"</span>
                        {task.instances > 1 && <span>{task.instances} instances</span>}
                      </>
                    )}
                  </div>
                </div>
                {editingId !== task.id && (
                  <button
                    onClick={() => startEditing(task)}
                    className="flex items-center text-sm text-blue-600 hover:text-blue-800 transition-colors"
                  >
                    <Edit2 className="w-4 h-4 mr-1" />
                    {task.sop ? 'Edit SOP' : 'Add SOP'}
                  </button>
                )}
              </div>

              {editingId === task.id ? (
                <div className="mt-4 space-y-3">
                  <textarea
                    value={editingSOP}
                    onChange={(e) => setEditingSOP(e.target.value)}
                    placeholder="Enter step-by-step instructions for completing this task..."
                    rows={6}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 resize-none"
                  />
                  <div className="flex justify-end gap-2">
                    <button
                      onClick={cancelEditing}
                      className="px-3 py-1.5 text-sm text-gray-700 hover:text-gray-900"
                    >
                      Cancel
                    </button>
                    <button
                      onClick={() => saveSOP(task)}
                      className="flex items-center px-3 py-1.5 text-sm text-white bg-blue-600 rounded-lg hover:bg-blue-700"
                    >
                      <Save className="w-4 h-4 mr-1" />
                      Save SOP
                    </button>
                  </div>
                </div>
              ) : task.sop ? (
                <div className="mt-3 p-3 bg-blue-50 rounded-lg">
                  <div className="flex items-start">
                    <ClipboardList className="w-4 h-4 text-blue-600 mr-2 mt-0.5 flex-shrink-0" />
                    <pre className="text-sm text-blue-900 whitespace-pre-wrap font-sans">{task.sop}</pre>
                  </div>
                </div>
              ) : (
                <div className="mt-3 p-3 bg-gray-50 rounded-lg text-sm text-gray-600 text-center">
                  No SOP defined
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}